/**
 * Video URL Builder
 * 
 * Constrói URLs de embed para diferentes providers de vídeo
 * baseado nos dados armazenados no banco de dados.
 */

/**
 * Constrói URL de embed para um provider específico
 * @param {string} provider - Provider do vídeo ('vimeo', 'youtube', 'bunny', 'native', 'iframe')
 * @param {Object} data - Dados do vídeo
 * @param {string} data.video_id - ID do vídeo (para vimeo, youtube, bunny)
 * @param {string} data.video_library_id - Library ID (apenas para bunny)
 * @param {string} data.library_url - URL completa (para native, iframe)
 * @param {Object} options - Opções adicionais
 * @param {boolean} options.autoplay - Se deve autoplay
 * @param {boolean} options.controls - Se deve mostrar controles
 * @param {string} options.start - Tempo de início (em segundos)
 * @returns {string|null} URL de embed ou null se inválida
 */
export function buildEmbedUrl(provider, data, options = {}) {
  if (!provider || !data) {
    console.warn('⚠️ VideoURLBuilder: Provider ou dados não fornecidos');
    return null;
  }

  const {
    autoplay = false,
    controls = true,
    start = 0,
    ...otherOptions
  } = options;

  try {
    switch (provider) {
      case 'vimeo':
        return buildVimeoUrl(data.video_id, { autoplay, start, ...otherOptions });
      
      case 'youtube':
        return buildYouTubeUrl(data.video_id, { autoplay, controls, start, ...otherOptions });
      
      case 'bunny':
        return buildBunnyUrl(data.video_library_id, data.video_id, { autoplay, controls, start, ...otherOptions });
      
      case 'native':
      case 'iframe':
        return data.library_url;
      
      default:
        console.warn(`⚠️ VideoURLBuilder: Provider não suportado: ${provider}`);
        return null;
    }
  } catch (error) {
    console.error(`❌ VideoURLBuilder: Erro ao construir URL para ${provider}:`, error);
    return null;
  }
}

/**
 * Constrói URL de embed do Vimeo
 * @param {string} videoId - ID do vídeo no Vimeo
 * @param {Object} options - Opções do player
 * @returns {string} URL de embed do Vimeo
 */
function buildVimeoUrl(videoId, options = {}) {
  if (!videoId) {
    throw new Error('Video ID é obrigatório para Vimeo');
  }

  const baseUrl = `https://player.vimeo.com/video/${videoId}`;
  const params = new URLSearchParams();

  // Parâmetros padrão para melhor compatibilidade
  params.set('api', '1');
  params.set('origin', window.location.origin);
  params.set('dnt', '1'); // Do Not Track
  params.set('transparent', '0'); // Evita problemas de transparência
  
  // Parâmetros de segurança e permissões
  params.set('responsive', '1'); // Player responsivo
  params.set('title', '0'); // Esconder título por padrão
  params.set('byline', '0'); // Esconder byline por padrão
  params.set('portrait', '0'); // Esconder retrato por padrão

  // Opções específicas
  if (options.autoplay) {
    params.set('autoplay', '1');
    params.set('muted', '1'); // Autoplay requer muted
  }

  if (options.start && options.start > 0) {
    params.set('t', `${options.start}s`);
  }

  if (options.background) {
    params.set('background', '1');
  }

  if (options.loop) {
    params.set('loop', '1');
  }

  if (options.muted) {
    params.set('muted', '1');
  }

  // Adicionar controles se não for background
  if (!options.background) {
    params.set('controls', '1');
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Constrói URL de embed do YouTube
 * @param {string} videoId - ID do vídeo no YouTube
 * @param {Object} options - Opções do player
 * @returns {string} URL de embed do YouTube
 */
function buildYouTubeUrl(videoId, options = {}) {
  if (!videoId) {
    throw new Error('Video ID é obrigatório para YouTube');
  }

  const baseUrl = `https://www.youtube.com/embed/${videoId}`;
  const params = new URLSearchParams();

  // Parâmetros padrão
  params.set('enablejsapi', '1');
  params.set('origin', window.location.origin);

  // Opções específicas
  if (options.autoplay) {
    params.set('autoplay', '1');
  }

  if (options.controls === false) {
    params.set('controls', '0');
  }

  if (options.start && options.start > 0) {
    params.set('start', Math.floor(options.start));
  }

  if (options.end) {
    params.set('end', Math.floor(options.end));
  }

  if (options.loop) {
    params.set('loop', '1');
    params.set('playlist', videoId); // Necessário para loop funcionar
  }

  if (options.muted) {
    params.set('mute', '1');
  }

  if (options.rel === false) {
    params.set('rel', '0');
  }

  if (options.showinfo === false) {
    params.set('showinfo', '0');
  }

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Constrói URL de embed do Bunny CDN
 * @param {string} libraryId - ID da biblioteca no Bunny
 * @param {string} videoId - ID do vídeo no Bunny
 * @param {Object} options - Opções do player
 * @returns {string} URL de embed do Bunny
 */
function buildBunnyUrl(libraryId, videoId, options = {}) {
  if (!libraryId || !videoId) {
    throw new Error('Library ID e Video ID são obrigatórios para Bunny CDN');
  }

  const baseUrl = `https://iframe.mediadelivery.net/embed/${libraryId}/${videoId}`;
  const params = new URLSearchParams();

  // Opções específicas do Bunny
  if (options.autoplay) {
    params.set('autoplay', 'true');
  }

  if (options.controls === false) {
    params.set('controls', 'false');
  }

  if (options.start && options.start > 0) {
    params.set('t', `${options.start}s`);
  }

  if (options.loop) {
    params.set('loop', 'true');
  }

  if (options.muted) {
    params.set('muted', 'true');
  }

  if (options.poster) {
    params.set('poster', options.poster);
  }

  if (options.preload) {
    params.set('preload', options.preload);
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Valida se os dados fornecidos são suficientes para construir a URL
 * @param {string} provider - Provider do vídeo
 * @param {Object} data - Dados do vídeo
 * @returns {boolean} Se os dados são válidos
 */
export function validateVideoData(provider, data) {
  if (!provider || !data) {
    return false;
  }

  switch (provider) {
    case 'vimeo':
    case 'youtube':
      return !!(data.video_id && typeof data.video_id === 'string');
    
    case 'bunny':
      return !!(
        data.video_id && 
        data.video_library_id && 
        typeof data.video_id === 'string' && 
        typeof data.video_library_id === 'string'
      );
    
    case 'native':
    case 'iframe':
      return !!(data.library_url && typeof data.library_url === 'string');
    
    default:
      return false;
  }
}

/**
 * Extrai informações do vídeo de uma URL (para migração/importação)
 * @param {string} url - URL do vídeo
 * @returns {Object|null} Dados extraídos ou null se inválida
 */
export function extractVideoInfo(url) {
  if (!url || typeof url !== 'string') {
    return null;
  }

  // Vimeo
  const vimeoMatch = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(?:.*#|.*\/videos\/)?([0-9]+)/);
  if (vimeoMatch) {
    return {
      provider: 'vimeo',
      video_id: vimeoMatch[1],
      original_url: url
    };
  }

  // YouTube
  const youtubeMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
  if (youtubeMatch && youtubeMatch[2].length === 11) {
    return {
      provider: 'youtube',
      video_id: youtubeMatch[2],
      original_url: url
    };
  }

  // Bunny CDN
  const bunnyMatch = url.match(/iframe\.mediadelivery\.net\/embed\/([^\/]+)\/([^\/\?]+)/);
  if (bunnyMatch) {
    return {
      provider: 'bunny',
      video_library_id: bunnyMatch[1],
      video_id: bunnyMatch[2],
      original_url: url
    };
  }

  // Iframe genérico
  if (url.includes('iframe') || url.includes('embed')) {
    return {
      provider: 'iframe',
      library_url: url,
      original_url: url
    };
  }

  // Native/arquivo direto
  return {
    provider: 'native',
    library_url: url,
    original_url: url
  };
}

/**
 * Constrói URL de embed a partir de um MediaMapping
 * @param {Object} mediaMapping - Objeto MediaMapping do banco
 * @param {Object} options - Opções do player
 * @returns {string|null} URL de embed ou null se inválida
 */
export function buildEmbedUrlFromMediaMapping(mediaMapping, options = {}) {
  if (!mediaMapping) {
    return null;
  }

  const { provider, video_id, video_library_id, library_url } = mediaMapping;
  
  return buildEmbedUrl(provider, {
    video_id,
    video_library_id,
    library_url
  }, options);
}
